import { useState, useEffect, SyntheticEvent } from "react";
import {
  Box, Grid, Card, CardContent, Divider, FormControl,
  TextField, InputLabel, MenuItem, Select, useMediaQuery,
  SelectChangeEvent, CircularProgress, IconButton, Tooltip, Badge
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import axios from "axios";
import Title from "../../components/Title";
import SearchIcon from '@mui/icons-material/Search';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import { useDrawer } from "../../components/context/DrawerContext";
import { stickyListRegionWidth } from "../../components/layouts/StickyListToolbar";
import SimpleCustomTable from "../../components/table/SimpleCustomTable";
import { SimpleColumn, SimpleRow, RowId } from "../../components/table/interface";
import HeaderResponsive from "../../components/layouts/HeaderResponsive";
import EmptyStateMessage from "../../components/layouts/EmptyStateMessage";
import { minYmd, nowOperationalDateString } from '../../utils/operationalDateTime';
import ModalDeletedRecordDetail from "./modals/ModalDeletedRecordDetail";
import ModalDeletedLendsList, { DeletedLendSummary } from "./modals/ModalDeletedLendsList";

interface Office { id: number; name: string; }
interface Collect { id: number; name: string; }

interface FilterState {
  startDate: string;
  endDate: string;
  officeId: string;
  collectId: string;
}

interface RegistrosBorradosProps {
  showAlert: (msg: string, severity?: "error" | "info" | "success" | "warning") => void;
  showLoading: () => void;
  hideLoading: () => void;
}

const formatDateTime = (value?: string): string => {
  if (!value) return "";
  const d = new Date(String(value).replace(" ", "T"));
  if (isNaN(d.getTime())) return value;
  return d.toLocaleString("es-CO");
};

const RegistrosBorrados: React.FC<RegistrosBorradosProps> = ({
  showAlert, showLoading, hideLoading,
}) => {
  document.title = "Registros Borrados";
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const currentDate = nowOperationalDateString();
  const { drawerOpen } = useDrawer();

  const [offices, setOffices] = useState<Office[]>([]);
  const [collectsOffice, setCollectsOffice] = useState<Collect[]>([]);
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState<SimpleRow[]>([]);

  const [detailOpen, setDetailOpen] = useState(false);
  const [detailBackupId, setDetailBackupId] = useState<number | null>(null);

  const [lendsListOpen, setLendsListOpen] = useState(false);
  const [lendsListClientName, setLendsListClientName] = useState<string | null>(null);
  const [lendsListItems, setLendsListItems] = useState<DeletedLendSummary[]>([]);
  const [lendsByClientId, setLendsByClientId] = useState<Record<number, DeletedLendSummary[]>>({});

  const openClientDetail = (backupId: number) => {
    setDetailBackupId(backupId);
    setDetailOpen(true);
  };

  const openLendsList = (clientId: number, clientName: string) => {
    setLendsListClientName(clientName);
    setLendsListItems(lendsByClientId[clientId] || []);
    setLendsListOpen(true);
  };

  const [filter, setFilter] = useState<FilterState>({
    startDate: currentDate,
    endDate: currentDate,
    officeId: "",
    collectId: "",
  });

  // Oficinas visibles para el usuario (ya vienen filtradas por permiso desde el backend; superadmin ve todas)
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get("/offices");
        if (data?.data) setOffices(data.data);
      } catch (e) {
        console.error(e);
        showAlert("Error al cargar las oficinas.", "error");
      }
    })();
  }, [showAlert]);

  // Cobros por oficina (también ya filtrados por permiso en el backend)
  useEffect(() => {
    (async () => {
      if (!filter.officeId) {
        setCollectsOffice([]);
        return;
      }
      try {
        const { data } = await axios.get(`/collectsoffice/${filter.officeId}`);
        setCollectsOffice(Array.isArray(data) ? data : []);
        setFilter((prev) => ({ ...prev, collectId: "" }));
      } catch (e) {
        console.error(e);
        showAlert("Error al cargar los cobros para la oficina.", "error");
      }
    })();
  }, [filter.officeId, showAlert]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFilter((prev) => ({ ...prev, [name]: value }));
  };
  const handleOfficeChange = (e: SelectChangeEvent<string>) => {
    setFilter((prev) => ({ ...prev, officeId: e.target.value }));
  };
  const handleCollectChange = (e: SelectChangeEvent<string>) => {
    setFilter((prev) => ({ ...prev, collectId: e.target.value }));
  };

  const validateForm = (): boolean => {
    if (new Date(filter.startDate) > new Date(filter.endDate)) {
      showAlert("La fecha de inicio no puede ser mayor a la fecha final.", "warning");
      return false;
    }
    return true;
  };

  const columns: SimpleColumn[] = [
    { field: 'description', headerName: 'Cliente', flex: 1.4, minWidth: 140, align: 'left', wrapText: true },
    { field: 'reference', headerName: 'Documento', flex: 1, minWidth: 110, align: 'center' },
    { field: 'office_name', headerName: 'Oficina', flex: 1, minWidth: 100, align: 'center' },
    { field: 'collect_name', headerName: 'Cobro', flex: 1, minWidth: 100, align: 'center' },
    { field: 'deleted_by_name', headerName: 'Borrado por', flex: 1.1, minWidth: 120, align: 'left' },
    {
      field: 'deleted_at', headerName: 'Fecha y hora', flex: 1.1, minWidth: 140, align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => formatDateTime(String(row.deleted_at ?? '')),
    },
    {
      field: 'actions', headerName: '', flex: 0.8, minWidth: 90, maxWidth: 100, align: 'center',
      renderCell: ({ row }: { row: SimpleRow }) => (
        <Box display="flex" gap={0.5} justifyContent="center">
          {row.client_deleted ? (
            <Tooltip title="Ver cliente borrado">
              <IconButton
                size="small"
                color="primary"
                onClick={(e) => {
                  e.stopPropagation();
                  openClientDetail(Number(row.client_backup_id));
                }}
              >
                <PersonIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          ) : null}
          {Number(row.lend_count || 0) > 0 ? (
            <Tooltip title={`Ver ${row.lend_count} préstamo(s) borrado(s)`}>
              <IconButton
                size="small"
                color="secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  openLendsList(Number(row.client_id), String(row.description ?? ''));
                }}
              >
                <Badge badgeContent={row.lend_count as number} color="secondary" max={99}>
                  <AttachMoneyIcon fontSize="small" />
                </Badge>
              </IconButton>
            </Tooltip>
          ) : null}
        </Box>
      ),
    },
  ];

  const handleSubmit = async (e: SyntheticEvent) => {
    e.preventDefault();
    if (!validateForm()) return;

    showLoading();
    setLoading(true);
    try {
      const payload = {
        start_date: filter.startDate,
        end_date: filter.endDate,
        collect_ids: filter.collectId ? [Number(filter.collectId)] : undefined,
      };

      const { data } = await axios.post("/inform/deletedrecords", payload);
      const items = Array.isArray(data?.data) ? data.data : [];

      if (items.length === 0) {
        setRows([]);
        setLendsByClientId({});
        showAlert("No se encontraron registros borrados en el período.", "info");
        return;
      }

      const nextLendsByClientId: Record<number, DeletedLendSummary[]> = {};

      const mapped: SimpleRow[] = items.map((r: any, i: number) => {
        const clientId = Number(r.client_id);
        nextLendsByClientId[clientId] = Array.isArray(r.lends) ? r.lends : [];

        return {
          id: `client-${clientId}-${i}` as RowId,
          client_id: clientId,
          client_deleted: Boolean(r.client_deleted),
          client_backup_id: r.client_backup_id ?? null,
          lend_count: Array.isArray(r.lends) ? r.lends.length : 0,
          description: r.description ?? "",
          reference: r.reference ?? "",
          office_name: r.office_name ?? "",
          collect_name: r.collect_name ?? "",
          deleted_by_name: r.deleted_by_name ?? "",
          deleted_at: r.deleted_at ?? "",
        };
      });

      setLendsByClientId(nextLendsByClientId);
      setRows(mapped);
      showAlert(`Se encontraron ${mapped.length} clientes con registros borrados.`, "success");
    } catch (error) {
      console.error("Error obteniendo registros borrados:", error);
      showAlert("Error al obtener los datos.", "error");
    } finally {
      hideLoading();
      setLoading(false);
    }
  };

  return (
    <HeaderResponsive sx={{ py: 0, px: 0.5, pb: { xs: 10, md: 8 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
        <Title>Registros Borrados</Title>
        <Box>
          <Box component="button" type="submit" form="form-registros-borrados" sx={{ display: 'none' }} />
        </Box>
      </Box>

      <Card sx={{ width: (t) => stickyListRegionWidth(t, drawerOpen), maxWidth: '100%', boxSizing: 'border-box', overflowX: 'auto', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
        <form id="form-registros-borrados" onSubmit={handleSubmit}>
          <CardContent
            sx={{
              width: (t) => stickyListRegionWidth(t, drawerOpen),
              maxWidth: '100%',
              boxSizing: 'border-box',
              overflowX: 'auto',
              flexShrink: 0,
            }}
          >
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth name="startDate" size="small" type="date" label="Fecha Inicio"
                  value={filter.startDate} onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }} inputProps={{ max: minYmd(filter.endDate, nowOperationalDateString()) }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  fullWidth name="endDate" size="small" type="date" label="Fecha Fin"
                  value={filter.endDate} onChange={handleDateChange}
                  InputLabelProps={{ shrink: true }} inputProps={{ min: filter.startDate, max: nowOperationalDateString() }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth>
                  <InputLabel>Oficina</InputLabel>
                  <Select value={filter.officeId} size="small" label="Oficina" onChange={handleOfficeChange}>
                    <MenuItem value="">
                      <em>Todas (según permisos)</em>
                    </MenuItem>
                    {offices.map((office) => (
                      <MenuItem key={office.id} value={String(office.id)}>
                        {office.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <FormControl fullWidth disabled={!filter.officeId}>
                  <InputLabel>Cobro</InputLabel>
                  <Select value={filter.collectId} size="small" label="Cobro" onChange={handleCollectChange}>
                    <MenuItem value="">
                      <em>Todos los cobros de la oficina</em>
                    </MenuItem>
                    {collectsOffice.map((collect) => (
                      <MenuItem key={collect.id} value={String(collect.id)}>
                        {collect.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>

              <Grid item xs={12}>
                <Box display="flex" justifyContent="flex-end">
                  <Box
                    component="button"
                    type="submit"
                    sx={{
                      display: 'inline-flex', alignItems: 'center', gap: 1,
                      px: 2, py: 1, border: 'none', borderRadius: 1,
                      bgcolor: 'primary.main', color: 'primary.contrastText', cursor: 'pointer',
                    }}
                  >
                    <SearchIcon fontSize="small" /> Buscar
                  </Box>
                </Box>
              </Grid>
            </Grid>
            <Divider sx={{ my: 1 }} />
          </CardContent>
        </form>

        <CardContent
          sx={{
            width: (t) => stickyListRegionWidth(t, drawerOpen),
            maxWidth: '100%',
            boxSizing: 'border-box',
            overflowX: 'auto',
            pt: 0,
            pb: 4,
          }}
        >
          {rows.length > 0 ? (
            <SimpleCustomTable
              rows={rows}
              columns={columns}
              countValue={rows.length}
              disableInternalScroll
              showBorders
              showCount
              exportCsv
              exportFileName="registros-borrados.csv"
              wrapCellContent
            />
          ) : (
            <EmptyStateMessage message="No existen registros" align="left" />
          )}
        </CardContent>
      </Card>
      <Box sx={{ height: { xs: 48, md: 32 } }} />

      {loading && (
        <Box sx={{
          position: 'fixed', inset: 0, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          bgcolor: 'rgba(0,0,0,0.12)', zIndex: 2000
        }}>
          <CircularProgress />
        </Box>
      )}

      <ModalDeletedRecordDetail
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        recordType="client"
        backupId={detailBackupId}
        showAlert={showAlert}
      />

      <ModalDeletedLendsList
        open={lendsListOpen}
        onClose={() => setLendsListOpen(false)}
        clientName={lendsListClientName}
        lends={lendsListItems}
        showAlert={showAlert}
      />
    </HeaderResponsive>
  );
};

export default RegistrosBorrados;
